import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { FaEllipsisV, FaShareAlt, FaBookmark, FaHeart, FaRetweet,FaEyeSlash,FaBan,FaFlag } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import "./SavedPosts.css";
import lazyGif from "../../assets/gifs/liquid_loading.gif";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { FiArrowLeft } from "react-icons/fi";

export default function SavedPosts() {
  const { translations: tRaw } = useLanguage();
  const t = useMemo(() => tRaw?.savedpostspage || {}, [tRaw]);
  const { currentUser, userProfile } = useAuth();
  const [savedPosts, setSavedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const generateShareId = useCallback(() => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let id = "";
    for (let i = 0; i < 8; i += 1) {
      const idx = Math.floor(Math.random() * chars.length);
      id += chars[idx];
    }
    return id;
  }, []);

  const copyToClipboard = useCallback(async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      throw new Error("Clipboard API unavailable");
    } catch (_) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (err) {
        console.error("[SavedPosts] Clipboard fallback failed:", err);
        throw err;
      }
    }
  }, []);

  const handleShare = useCallback(async (post) => {
    if (!post?.id) return;
    if (!currentUser?.id) {
      alert(t?.shareRequiresLogin || "Please sign in to share this post.");
      return;
    }

    const ownerUsername = post?.author?.username;
    const ownerId = post?.author?.id;
    if (!ownerId || !ownerUsername) {
      console.error('[SavedPosts] Missing post owner information for sharing.', { ownerId, ownerUsername, post });
      alert(t?.shareError || 'Unable to share this post right now.');
      return;
    }

    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const shareId = generateShareId();
      const shareBase = process.env.REACT_APP_SHARE_BASE_URL;
      // SPA view URL (used within site and stored to DB)
      const viewUrl = (typeof window !== 'undefined')
        ? `${window.location.origin.replace(/\/$/, '')}/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}`
        : `/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}`;
      // External share URL (for social crawlers); fall back to view URL if base missing
      const shareUrl = shareBase
        ? `${shareBase.replace(/\/$/, '')}/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}`
        : viewUrl;
      const payload = {
        post_id: post.id,
        share_id: shareId,
        share_url: viewUrl,
        sharer_id: currentUser.id
      };

      const { error } = await supabase.from('shares').insert(payload);
      if (!error) {
        const title = t?.shareTitle || "Check out this post!";

        if (navigator.share) {
          try {
            await navigator.share({ title, url: shareUrl });
            return;
          } catch (shareError) {
            console.warn('[SavedPosts] navigator.share failed, falling back to clipboard', shareError);
          }
        }

        try {
          await copyToClipboard(shareUrl);
          alert(t?.shareCopied || "Link copied to clipboard!");
        } catch (copyError) {
          console.error('[SavedPosts] Copy to clipboard failed:', copyError);
          alert(t?.shareError || 'Unable to share this post right now.');
        }
        return;
      }

      lastError = error;
      if (!error?.code || error.code !== '23505') {
        break;
      }
    }

    console.error('[SavedPosts] Failed to create share record:', lastError);
    alert(t?.shareError || 'Unable to share this post right now.');
  }, [copyToClipboard, currentUser?.id, generateShareId, t]);

  const loadSavedPosts = useCallback(async (uid) => {
    if (!uid) return;
    
    try {
      setLoading(true);
      
      // Fetch saved posts from saves table
      const { data: savesData, error: savesError } = await supabase
        .from('saves')
        .select('post_id, created_at')
        .eq('user_id', uid)
        .order('created_at', { ascending: false });

      if (savesError) {
        console.error("Error fetching saves:", savesError);
        setSavedPosts([]);
        setLoading(false);
        return;
      }

      if (!savesData || savesData.length === 0) {
        setSavedPosts([]);
        setLoading(false);
        return;
      }

      // Extract post IDs
      const postIds = savesData.map(save => save.post_id).filter(Boolean);

      if (postIds.length === 0) {
        setSavedPosts([]);
        setLoading(false);
        return;
      }

      // Fetch posts from posts table
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .in('id', postIds)
        .eq('is_archived', false);

      if (postsError) {
        console.error("Error fetching posts:", postsError);
        setSavedPosts([]);
        setLoading(false);
        return;
      }

      // Get unique user IDs from posts
      const userIds = [...new Set((postsData || []).map(post => post.user_id).filter(Boolean))];

      // Fetch author profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, username, avatar_url')
        .in('user_id', userIds);

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      }

      // Create a map of profiles by user_id
      const profilesMap = new Map();
      (profilesData || []).forEach(profile => {
        // Resolve avatar URL
        let avatarUrl = '/default-avatar.png';
        if (profile.avatar_url) {
          try {
            const path = `${profile.user_id}/${profile.avatar_url}`;
            const { data } = supabase.storage.from('avatars').getPublicUrl(path);
            avatarUrl = data?.publicUrl || avatarUrl;
          } catch {
            avatarUrl = '/default-avatar.png';
          }
        }

        profilesMap.set(profile.user_id, {
          id: profile.user_id,
          username: profile.display_name || profile.username || 'User',
          profileImage: avatarUrl
        });
      });

      // Map posts to include author info and saved timestamp
      const loadedPosts = (postsData || []).map(post => {
        const savedAt = savesData.find(s => s.post_id === post.id)?.created_at;
        const author = profilesMap.get(post.user_id) || {
          id: post.user_id,
          username: `user_${post.user_id.slice(0, 8)}`,
          profileImage: '/default-avatar.png'
        };

        // Resolve media URLs
        const media = (post.media_names || []).map(filename => {
          const isVideo = filename.includes('.mp4') || filename.includes('.mov') || filename.includes('.webm');
          try {
            const path = `${post.user_id}/${filename}`;
            const { data } = supabase.storage.from('post-media').getPublicUrl(path);
            return {
              type: isVideo ? 'video' : 'image',
              url: data?.publicUrl || ''
            };
          } catch {
            return {
              type: isVideo ? 'video' : 'image',
              url: ''
            };
          }
        }).filter(m => m.url);

        return {
          id: post.id,
          ...post,
          postedById: post.user_id,
          media,
          author,
          savedAt
        };
      });

      // Sort by saved timestamp (most recent first)
      loadedPosts.sort((a, b) => {
        if (a.savedAt && b.savedAt) {
          return new Date(b.savedAt) - new Date(a.savedAt);
        }
        return 0;
      });

      setSavedPosts(loadedPosts);
    } catch (err) {
      console.error(t?.loadError || "Error loading saved posts:", err);
      setSavedPosts([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!currentUser?.id) {
      navigate("/");
      return;
    }
    loadSavedPosts(currentUser.id);
  }, [navigate, loadSavedPosts, currentUser?.id]);

  if (loading) return <div className="loading">{t?.loading || "Loading..."}</div>;

  return (
    <div className="saved-posts-wrapper">
      <FloatingSidebarButton userProfile={userProfile} />
      <button
        className="saves-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Back"
        title="Back"
      >
        <FiArrowLeft />
      </button>
      <div className="saved-posts-header">
        <h2>{t?.title || "Saved Posts"}</h2>
        <p>{t?.subtitle || "Posts you've saved for later"}</p>
      </div>

      <div className="saved-posts-feed">
        {savedPosts.length === 0 ? (
          <div className="empty-state">
            <img src={lazyGif} alt={t?.emptyAlt || "No saved posts"} />
            <p>{t?.emptyState || "No Saved Posts Yet"}</p>
          </div>
        ) : (
          savedPosts.map(post => (
            <SavedPostCard
              key={post.id}
              post={post}
              setSavedPosts={setSavedPosts}
              t={t}
              navigate={navigate}
              onShare={handleShare}
            />
          ))
        )}
      </div>
    </div>
  );
}

function SavedPostCard({ post, setSavedPosts, t, navigate, onShare }) {
  const { currentUser } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const toggleMore = () => setMoreOpen(o => !o);

  const moreRef = React.useRef(null);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!moreOpen) return;

    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setMoreOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [moreOpen]);

  const rawMedia = Array.isArray(post.media) ? post.media : [];
  const mediaItems = rawMedia.map(item => 
    typeof item === "string" ? JSON.parse(item) : item
  ).filter(Boolean);

  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [post.id]);

  const prev = (e) => {
    e.stopPropagation();
    if (mediaItems.length < 2) return;
    setIdx(i => (i - 1 + mediaItems.length) % mediaItems.length);
  };
  const next = (e) => {
    e.stopPropagation();
    if (mediaItems.length < 2) return;
    setIdx(i => (i + 1) % mediaItems.length);
  };

  const current = mediaItems[idx];

  return (
    <div className="saved-post-card">
      <div className="saved-post-header">
        <div className="saved-author-info" onClick={() => navigate(`/profile/${post?.author?.id}`)}>
          <img 
            src={post.author.profileImage || ""} 
            alt={post.author.username} 
            className="saved-author-avatar"
          />
          <span className="saved-author-username">{post.author.username}</span>
        </div>
        <div className="saved-post-actions">
          <button className="action-btn saved-repost">
            <FaRetweet />
          </button>
          <div className="more-wrapper" ref={moreRef}>
            <button className="action-btn more-btn" onClick={toggleMore}>
              <FaEllipsisV />
            </button>
            {moreOpen && (
              <div className="saved-posts-dropdown-menu">
                <div className="dropdown-item"><FaEyeSlash /> {t?.notInterested || "Not Interested"}</div>
                <div className="dropdown-item"><FaBan /> {t?.blockUser || "Block User"}</div>
                <div className="dropdown-item"><FaFlag /> {t?.reportPost || "Report Post"}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {post.caption && (
        <div className="post-caption">
          <p>{post.caption}</p>
        </div>
      )}

      <div className="saved-post-media" style={{ position: 'relative' }}>
        <div className="post-media-frame" style={{ position: 'relative' }}>
          {mediaItems.length > 1 && (
            <button className="saved-carousel-arrow left" onClick={prev}>‹</button>
          )}

          {current?.type === "image" && (
            <img src={current.url} alt="" className="saved-post-image" />
          )}
          {current?.type === "video" && (
            <video controls src={current.url} className="saved-post-video" />
          )}

          {mediaItems.length > 1 && (
            <button className="saved-carousel-arrow right" onClick={next}>›</button>
          )}
        </div>
      </div>

      <div className="post-footer">
        <button className="footer-btn">
          <FaHeart className="like-icon" />
        </button>
        <button
          className="footer-btn"
          onClick={() => onShare?.(post)}
        >
          <FaShareAlt className="share-icon" />
        </button>
        <button
          className="footer-btn"
          onClick={async () => {
            try {
              if (!currentUser?.id) return;

              // Delete from saves table
              const { error } = await supabase
                .from('saves')
                .delete()
                .eq('user_id', currentUser.id)
                .eq('post_id', post.id);

              if (error) {
                console.error(t?.unsaveError || "Failed to unsave post:", error);
                return;
              }

              setSavedPosts(prev => prev.filter(p => p.id !== post.id));
            } catch (err) {
              console.error(t?.unsaveError || "Failed to unsave post:", err);
            }
          }}
        >
          <FaBookmark className="saved-icon" />
        </button>
      </div>
    </div>
  );
}